import { AlertTriangle, CheckCircle2, Clock3, MinusCircle, XCircle, type LucideIcon } from "lucide-react";

/** `ToFollow`: a required document the applicant may provide after the filing; it is requested at registration and does not hold up the review. */
export type CompletenessStatus = "Present" | "Deficient" | "Missing" | "ToFollow" | "NotRequired";
export type CompletenessSource = "FormalReview" | "Readiness" | "Override";
export type OverrideStatus = "Present" | "NotRequired";

/** An officer's active ruling on a checklist item; when present it decided the row's status. */
export interface CompletenessOverride { id: string; status: OverrideStatus; reason: string; setByName: string; setByRole?: string | null; setAt: string }

export interface CompletenessItem {
  key: string;
  titleEn: string;
  titleAr: string;
  mandatory: boolean;
  status: CompletenessStatus;
  finding?: string | null;
  findingAr?: string | null;
  documentId?: string | null;
  fileName?: string | null;
  page?: number | null;
  requestText?: string | null;
  requestTextAr?: string | null;
  /** Who decided the status: the readiness rules, the Initial Review Agent, or an officer override. */
  source: CompletenessSource;
  /** Document category the slot files under (FinancialStatements, PowerOfAttorney …). */
  category?: string | null;
  override?: CompletenessOverride | null;
  /** Working days the applicant has after registration when the item may follow the filing. */
  canFollowDays?: number | null;
}

export interface CompletenessView {
  caseType: string;
  basis: string;
  basisAr: string;
  /** Null when nothing is recorded: the panel says "not recorded", never 0 or 100. */
  score: number | null;
  scoreAtSubmission?: number | null;
  complete: boolean;
  mandatoryTotal: number;
  mandatoryPresent: number;
  items: CompletenessItem[];
  findings: string[];
  findingsAr: string[];
  formalReview?: { proposalId: string; status: string; createdAt: string; agentName: string; agentNameAr?: string | null; kind: string; summaryEn: string; summaryAr: string; confidence: number; proposedActionType?: string | null; workflowState?: string | null; reviewerName?: string | null; reviewedAt?: string | null } | null;
  feePaid: boolean;
  /** Who settled what the panel shows: the rules alone, the agent's unapproved proposal, or an officer (with name and time). */
  determinedBy?: "Rules" | "AgentProposal" | "Officer";
  determinedByName?: string | null;
  determinedAt?: string | null;
  methodVersion?: string;
}

export const completenessStatusMeta: Record<CompletenessStatus, { icon: LucideIcon; tone: string; badge: "success" | "warning" | "danger" | "info" | "outline" }> = {
  Present: { icon: CheckCircle2, tone: "text-success-600", badge: "success" },
  Deficient: { icon: AlertTriangle, tone: "text-warning-600", badge: "warning" },
  Missing: { icon: XCircle, tone: "text-danger-600", badge: "danger" },
  ToFollow: { icon: Clock3, tone: "text-seablue-700", badge: "info" },
  NotRequired: { icon: MinusCircle, tone: "text-sand-400", badge: "outline" },
};

/** Query key of the completeness view; every override, upload or re-link invalidates it together with the review. */
export const completenessKey = (caseId: string) => ["case-completeness", caseId] as const;

/** Everything that changes when a checklist item is resolved: the checklist, the documents, the review that reads them, and the timeline. */
export const completenessDependents = (caseId: string): readonly (readonly string[])[] => [
  completenessKey(caseId), ["case-documents", caseId], ["case-review", caseId], ["case-events", caseId], ["case-audit", caseId], ["case", caseId],
];

/** Whether a checklist item can be acted on from the workbench (a Present item can still be waived or re-filed). */
export const isActionable = (item: CompletenessItem) => item.status === "Missing" || item.status === "Deficient";
